import type { GroupLifeQuoteDetails } from '@/services/group-life';
import type { GroupLifeProposalStepName } from '@/services/proposals/group-life';
import { isTerminalPortalStatus } from '@/shared/enums/portal-status.enum';
import { toaster } from '@/shared/ui/toaster.component';
import { Steps, Text } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { Info } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
  useCalculateGroupLifeProposal,
  useSaveGroupLifeProposalStep,
} from '../../hooks/group-life';
import { usePmeDocusignSignaturesStatus } from '../../hooks/pme/use-pme-docusign-signatures.hook';
import { ModalProposalInAnalysis } from '../proposal-steps/modal-proposal-in-analysis.component';
import type { PmeProposalStepProps } from './proposal.types';
import {
  isPmeProposalEditable,
  isPmeProposalSignatureOnly,
} from './utils/pme-proposal-lock.util';
import {
  canReachPmeStep,
  isPmeInsuranceStepComplete,
  isPmePaymentComplete,
  resolvePmeEditStartStep,
} from './utils/pme-wizard-steps.util';
import { StepPmeInsureData } from './steps/step-insure-data.component';
import {
  pmePaymentDefaultValues,
  StepPmePayment,
  type PmePaymentValues,
} from './steps/step-payment.component';
import { StepPmePlan } from './steps/step-plan.component';
import { StepPmeResume } from './steps/step-resume.component';
import { StepPmeSignature } from './steps/step-signature.component';

const PME_STEP_NAMES = [
  'Dados do Seguro',
  'Plano',
  'Pagamento',
  'Resumo da Proposta',
  'Assinatura',
];

/** Índice da etapa de Assinatura — última do wizard. */
const SIGNATURE_STEP_INDEX = PME_STEP_NAMES.length - 1;
/** Índice do Resumo da Proposta — onde o wizard abre por padrão. */
const RESUME_STEP_INDEX = SIGNATURE_STEP_INDEX - 1;

const STEP_NAME_BY_INDEX: Record<number, GroupLifeProposalStepName> = {
  0: 'insurance',
  1: 'plan',
  2: 'payment',
  3: 'resume',
};

type Props = {
  policyId: string;
  productCode?: string;
  proposalNumber?: string;
  /** Snapshot completo da cotação (mesmo `GroupLifeQuoteDetails` da cotação). */
  details?: GroupLifeQuoteDetails;
  mode: 'edit' | 'view';
  startStep?: 'insurance' | 'resume';
};

export function PmeProposalWizard({
  policyId,
  productCode,
  proposalNumber,
  details,
  mode,
  startStep = 'resume',
}: Props) {
  const { save, saving } = useSaveGroupLifeProposalStep(policyId);

  const signaturesQuery = usePmeDocusignSignaturesStatus(policyId);
  const hasSignaturesStarted = Boolean(signaturesQuery.data?.envelopes?.length);

  /**
   * Proposta travada: nada mais é editável. Com a assinatura iniciada o status
   * já é "Em assinatura" (12) e alterar prêmio invalidaria as assinaturas
   * coletadas — por isso `hasSignaturesStarted` também trava, cobrindo a janela
   * em que o snapshot em cache ainda diz 6.
   *
   * Quem decide é o STATUS, não a rota. O "Visualizar" de uma proposta em
   * digitação (6) ou aceita (7) abre o Resumo funcional — com Confirmar,
   * gestão de vidas e navegação pelos steps — porque é de lá que o corretor
   * segue o fluxo depois da aprovação da subscrição. A rota de visualização
   * continua distinguível pelo `isReadOnlyView`, que é o que barra as ações da
   * própria assinatura.
   */
  const isViewMode = !isPmeProposalEditable({
    statusPortal: details?.statusPortal,
    hasSignaturesStarted,
  });

  /**
   * Proposta "Em assinatura" (12): o wizard vira uma tela só, a de Assinatura.
   *
   * Aprovada a proposta, a assinatura digital já foi disparada — não há mais
   * nada para conferir nem editar, e abrir o Resumo (ou a trilha de steps) só
   * daria ao corretor caminhos de volta para dados que estão fechados. Por isso
   * a trilha some, o "Voltar" some e o wizard abre direto na Assinatura.
   *
   * `hasSignaturesStarted` entra junto para cobrir a janela em que o snapshot
   * em cache ainda diz 6 mas os envelopes já existem. Proposta encerrada
   * (cancelada, expirada, recusada) fica de fora: o envelope antigo não reabre
   * a assinatura.
   */
  const isSignatureOnly = isPmeProposalSignatureOnly({
    statusPortal: details?.statusPortal,
    hasSignaturesStarted,
  });

  /**
   * Proposta ENCERRADA: expirada, recusada pela inspeção, recusada pela análise
   * ou recusada na subscrição da cotação (status 2, que a timeline da análise
   * rotula "Recusada").
   *
   * A pergunta é a combinada (`isTerminalPortalStatus`) porque a proposta pode
   * carregar tanto um código de proposta quanto um de cotação, e em nenhum
   * deles ela volta a ser editável: a trilha de steps não clica e o "Voltar"
   * dos rodapés fica desabilitado. O caminho adiante é uma proposta nova.
   */
  const isTerminalProposal = isTerminalPortalStatus(details?.statusPortal);

  const [effectiveDate, setEffectiveDate] = useState(
    details?.effectiveDate ?? ''
  );
  // O wizard só monta com `details` carregado (a página segura o loading), então
  // a inicialização preguiçosa basta para hidratar o step: o pagamento vem do
  // InsureMO, derivado das parcelas da apólice.
  const [paymentDraft, setPaymentDraft] = useState<PmePaymentValues>(() => ({
    ...pmePaymentDefaultValues,
    ...(details?.payment ?? {}),
  }));
  // Etapas gravadas pelo Próximo nesta sessão — o refetch dos detalhes pode
  // ainda não refletir o que acabou de ser salvo.
  const [sessionSavedSteps, setSessionSavedSteps] = useState<
    GroupLifeProposalStepName[]
  >([]);

  // Steps concluídos — e com todos os anteriores concluídos — aceitam navegação
  // direta pelo clique no número. Os demais continuam alcançáveis só pelo fluxo
  // sequencial (Avançar/Voltar).
  //
  // Dados do Seguro e Pagamento só contam quando GRAVADOS pelo corretor
  // (`completedSteps` da Api, ou o Próximo desta sessão) e completos. O
  // snapshot sozinho não basta: vigência e pagamento já vêm preenchidos da
  // cotação. O rascunho que o Voltar guarda também não: com ele a trilha
  // levaria ao Resumo sem pagamento persistido.
  const completedSteps = details?.completedSteps ?? [];
  const insuranceFilled =
    sessionSavedSteps.includes('insurance') ||
    (completedSteps.includes('insurance') &&
      isPmeInsuranceStepComplete({
        effectiveDate,
        stipulator: details?.stipulator,
        subStipulators: details?.subStipulators,
      }));
  const planFilled = Boolean(details?.plan);
  const paymentFilled =
    sessionSavedSteps.includes('payment') ||
    (completedSteps.includes('payment') &&
      isPmePaymentComplete(details?.payment));
  const stepFilled = [
    insuranceFilled,
    planFilled,
    paymentFilled,
    insuranceFilled && planFilled && paymentFilled,
  ];

  /** Etapas anteriores ao Resumo ainda pendentes — barram o Confirmar. */
  const pendingStepNames = PME_STEP_NAMES.slice(0, RESUME_STEP_INDEX).filter(
    (_name, index) => !stepFilled[index]
  );

  /**
   * Fora da assinatura o wizard abre no RESUMO: é de lá que o corretor confere
   * a proposta e gera os documentos. Mas só quando as etapas anteriores estão
   * concluídas — proposta recém-gerada abre na primeira etapa pendente. Vale
   * para o Editar E para o Visualizar de uma proposta editável: nenhuma rota
   * (nem `?startStep=resume`) pula as etapas.
   */
  const [currentStep, setCurrentStep] = useState(() => {
    if (isSignatureOnly) return SIGNATURE_STEP_INDEX;
    if (isViewMode) return RESUME_STEP_INDEX;
    if (startStep === 'insurance') return 0;
    return resolvePmeEditStartStep({
      stepFilled,
      resumeStepIndex: RESUME_STEP_INDEX,
    });
  });
  const [hasSetInitialStep, setHasSetInitialStep] = useState(isViewMode);

  // O status dos envelopes chega depois do snapshot: quando ele confirma que a
  // assinatura já começou, o wizard salta para a Assinatura.
  useEffect(() => {
    if (!hasSetInitialStep && hasSignaturesStarted) {
      setCurrentStep(SIGNATURE_STEP_INDEX);
      setHasSetInitialStep(true);
    }
  }, [hasSetInitialStep, hasSignaturesStarted]);

  const navigate = useNavigate();
  const calculateProposal = useCalculateGroupLifeProposal(policyId);
  const [sentToAnalysisOpen, setSentToAnalysisOpen] = useState(false);
  const [isGatingCalc, setIsGatingCalc] = useState(false);

  // Proposta travada não tem "etapa anterior" para onde voltar — o Voltar
  // devolve o corretor para a lista de propostas. Em assinatura não há Voltar
  // nenhum: a tela de Assinatura é a única do wizard.
  const canGoBack =
    !isSignatureOnly && !isTerminalProposal && (isViewMode || currentStep > 0);
  const canGoNext = currentStep < PME_STEP_NAMES.length - 1;

  const advance = () => {
    if (canGoNext) setCurrentStep(step => step + 1);
  };

  const handleNext = async (payload?: Record<string, unknown>) => {
    const stepName = STEP_NAME_BY_INDEX[currentStep];
    if (
      stepName === 'insurance' &&
      typeof payload?.effectiveDate === 'string'
    ) {
      setEffectiveDate(payload.effectiveDate);
    }
    if (stepName === 'payment' && payload) {
      setPaymentDraft({
        billingFrequency: String(
          payload.billingFrequency ?? pmePaymentDefaultValues.billingFrequency
        ),
        paymentMethod: String(
          payload.paymentMethod ?? pmePaymentDefaultValues.paymentMethod
        ),
        billingCut: String(payload.billingCut ?? ''),
        dueDay: String(payload.dueDay ?? ''),
      });
    }
    if (stepName && payload && Object.keys(payload).length > 0) {
      const { ok, error } = await save(stepName, payload);
      if (!ok) {
        toaster.create({
          type: 'error',
          title: 'Erro ao salvar',
          description:
            error?.trim() ||
            'Não foi possível salvar esta etapa. Tente novamente.',
        });
        return;
      }
      setSessionSavedSteps(steps =>
        steps.includes(stepName) ? steps : [...steps, stepName]
      );
    }
    advance();
  };

  const handleBack = () => {
    // Da Assinatura o Voltar devolve ao Resumo — é a etapa anterior real, e é
    // de lá que saem os documentos (Gerar PDF e Carta Comercial).
    if (currentStep > RESUME_STEP_INDEX) {
      setCurrentStep(step => step - 1);
      return;
    }

    // No Resumo de uma proposta TRAVADA não há etapa anterior para editar:
    // plano, pagamento e vidas estão fechados, então o Voltar sai para a lista.
    if (isViewMode) {
      void navigate({ to: '/proposals' });
      return;
    }

    if (canGoBack) {
      setCurrentStep(step => step - 1);
    }
  };

  /**
   * Recalcula o prêmio depois de mexer nas vidas. Só isso: **não** encaminha a
   * proposta para análise nem tira o corretor do fluxo. A decisão de ir (ou
   * não) para a subscrição é do Confirmar — o backend roda lá as validações de
   * subscrição sobre as vidas e responde `sentToUnderwritingAnalysis`.
   *
   * O recálculo também não pede `forceMemberRecalculation`: toda vida gravada
   * já sai tarifada, e re-tarifar a massa inteira a cada inclusão era o que
   * fazia a importação demorar minutos.
   */
  const recalculateProposal = useCallback(() => {
    setIsGatingCalc(true);
    void (async () => {
      try {
        await calculateProposal.mutateAsync(undefined);
      } catch (error) {
        toaster.create({
          type: 'error',
          title: 'Erro ao recalcular a proposta',
          description:
            error instanceof Error ? error.message : 'Tente novamente.',
        });
      } finally {
        setIsGatingCalc(false);
      }
    })();
  }, [calculateProposal]);

  /**
   * Alteração de vida (manual ou por planilha). O recálculo só roda quando a
   * alteração muda a tarifa: trocar apenas nome e/ou CPF, mantendo a mesma
   * quantidade de vidas, não mexe no prêmio — e recalcular nesse caso era
   * minutos de espera para chegar exatamente no mesmo valor.
   */
  const handleLivesOutcome = useCallback(
    ({ requiresRecalculation }: { requiresRecalculation: boolean }) => {
      if (!requiresRecalculation) return;
      recalculateProposal();
    },
    [recalculateProposal]
  );

  const stepProps: PmeProposalStepProps = {
    onNext: handleNext,
    onBack: handleBack,
    canGoBack,
    canGoNext,
    saving,
    isViewMode,
    isReadOnlyView: mode === 'view',
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <StepPmeInsureData
            {...stepProps}
            stipulator={details?.stipulator}
            subStipulators={details?.subStipulators}
            initialEffectiveDate={effectiveDate}
          />
        );
      case 1:
        return (
          <StepPmePlan
            {...stepProps}
            details={details}
            productCode={productCode}
          />
        );
      case 2:
        return (
          <StepPmePayment
            {...stepProps}
            values={paymentDraft}
            onValuesChange={setPaymentDraft}
          />
        );
      case SIGNATURE_STEP_INDEX:
        return (
          <StepPmeSignature
            {...stepProps}
            policyId={policyId}
            details={details}
            productCode={productCode}
            proposalNumber={proposalNumber}
            effectiveDate={effectiveDate}
            onSentToAnalysis={() => setSentToAnalysisOpen(true)}
          />
        );
      case 3:
        return (
          <StepPmeResume
            {...stepProps}
            policyId={policyId}
            details={details}
            productCode={productCode}
            effectiveDate={effectiveDate}
            proposalNumber={proposalNumber}
            onSentToAnalysis={() => setSentToAnalysisOpen(true)}
            onLivesChange={handleLivesOutcome}
            onImportCompleted={handleLivesOutcome}
            isRecalculating={isGatingCalc}
            onRecalculate={recalculateProposal}
            pendingStepNames={pendingStepNames}
          />
        );
      default:
        return null;
    }
  };

  const isClonedProposal = details?.clonedFrom?.[0]?.kind === 'PROPOSAL';

  const canJumpToStep = (index: number) =>
    !isTerminalProposal &&
    index !== currentStep &&
    (isViewMode || canReachPmeStep({ stepFilled, index }));

  /**
   * Proposta encerrada não tem etapa de Assinatura: ela nunca vai ser assinada,
   * e deixar o passo na trilha anuncia um caminho que não existe. Como a
   * Assinatura é a ÚLTIMA etapa, cortar o fim da lista não desalinha os
   * índices das demais.
   */
  const visibleStepNames = isTerminalProposal
    ? PME_STEP_NAMES.slice(0, SIGNATURE_STEP_INDEX)
    : PME_STEP_NAMES;

  return (
    <div className='flex flex-col gap-8'>
      {isClonedProposal && (
        <div className='flex items-center gap-3 rounded-xl border border-alba-blue-500 bg-alba-blue-50 px-4 py-3'>
          <Info className='h-5 w-5 shrink-0 text-alba-blue-500' />
          <Text className='text-sm text-alba-black-500'>
            Proposta clonada — o plano (modalidade, capitais e coberturas) é
            herdado da proposta de origem e não pode ser alterado. Data de
            vencimento, comissionamento e vidas seguem editáveis.
          </Text>
        </div>
      )}
      {/* Em assinatura a trilha some por inteiro: ela é o único caminho restante
          para as etapas de edição, e a proposta já está fechada. */}
      {!isSignatureOnly && (
        <Steps.Root
          step={currentStep}
          count={visibleStepNames.length}
          linear={false}
          onStepChange={event => {
            if (canJumpToStep(event.step)) setCurrentStep(event.step);
          }}
        >
          <Steps.List>
            {visibleStepNames.map((name, index) => (
              <Steps.Item key={name} index={index}>
                <Steps.Trigger
                  className={
                    canJumpToStep(index)
                      ? 'cursor-pointer'
                      : 'pointer-events-none cursor-default'
                  }
                >
                  <Steps.Indicator />
                  <Steps.Title>{name}</Steps.Title>
                </Steps.Trigger>
                {index < visibleStepNames.length - 1 && <Steps.Separator />}
              </Steps.Item>
            ))}
          </Steps.List>
        </Steps.Root>
      )}

      <div className='relative isolate flex w-full flex-col gap-8 rounded-2xl bg-white p-8 shadow-md'>
        {/* O recálculo do PME pode demorar (tarifa vida a vida). A página
            permanece inteira e utilizável — quem sinaliza o recálculo é o
            card de prêmio, já que são os VALORES que ficam indisponíveis. */}
        {renderStep()}
      </div>

      <ModalProposalInAnalysis
        isOpen={sentToAnalysisOpen}
        onClose={() => {
          setSentToAnalysisOpen(false);
          void navigate({ to: '/proposals' });
        }}
      />
    </div>
  );
}
